/* eslint-disable react/prop-types */
import React, { useEffect, useState, useContext } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import FormLabel from '@material-ui/core/FormLabel';
import FormGroup from '@material-ui/core/FormGroup';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Radio from '@material-ui/core/Radio';
import Box from '@material-ui/core/Box';
import Skeleton from '@material-ui/lab/Skeleton';

import { useRecoilValue } from 'recoil';
import currentFormAtom from '../../../../../../Atoms/currentForm';

import safelyParseJSON from '../../../../../../Utils/safelyParseJSON';
import { getOptionsAsync } from '../../../../../../Utils/engine/triggerApi';
import {
  IterationContext,
  getFieldDynamicDataState,
} from '../../../../../../Utils/engine/iteration';

const useStyles = makeStyles({
  skeletonBox: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 8,
  },
  skeletonCheckBox: {
    marginLeft: 8,
    marginRight: 16,
  },
  skeletonCheckLabel: {
    marginRight: 8,
  },
});

const RadioField = ({
  schema,
  idSchema,
  name,
  uiSchema,
  disabled,
  formData,
  readonly,
  required,
  onChange,
  errorSchema,
  onBlur,
  onFocus,
}) => {
  const classes = useStyles();

  const currentForm = useRecoilValue(currentFormAtom);
  const iterationIndexes = useContext(IterationContext);
  // Options chargées par l'extension dynamicData (propres à l'itération courante)
  const dynamicState = getFieldDynamicDataState(schema, iterationIndexes);
  const [enumOptions, setEnumOptions] = useState([]);
  const [currentValue, setCurrentvalue] = useState(
    safelyParseJSON(formData) ?? ''
  );
  const id = idSchema?.$id || name || null;

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (dynamicState['@paramsAtFetch'] && dynamicState['@fetched']) {
      setEnumOptions(dynamicState['@fetched']);
    }
    if (dynamicState['@isFetching'] === true) {
      setIsLoading(true);
    } else if (dynamicState['@isFetching'] === false) {
      setIsLoading(false);
    }
  }, [currentForm.dynamicData.updatedTimestamp]);

  useEffect(() => {
    setIsLoading(true);
    if (
      !schema?.['@dynamicData'] ||
      !dynamicState['@paramsAtFetch'] ||
      !dynamicState['@fetched']
    ) {
      getOptionsAsync(schema['@dynamicData']).then((filteredOptions) => {
        setEnumOptions(filteredOptions);
      });
    }
    setIsLoading(false);
  }, []);

  // Resynchronise la valeur affichée quand le formData change depuis l'extérieur
  // (ex : remise à zéro par l'extension dynamicData quand la source change)
  useEffect(() => {
    setCurrentvalue(safelyParseJSON(formData) ?? '');
  }, [formData]);

  const _onChange = (_e, value) => {
    setCurrentvalue(`${value}`);
    onChange(`${value}`, errorSchema, 2);
  };

  const _onBlur = ({ target: { value } }) => onBlur(id, value);

  const _onFocus = ({ target: { value } }) => onFocus(id, value);

  const row = uiSchema ? uiSchema?.['ui:options']?.inline : false;

  return (
    <>
      <FormLabel htmlFor={id} required={required}>
        {schema.title}
      </FormLabel>
      {!isLoading ? (
        <>
          <RadioGroup
            id={id}
            row={row}
            value={`${currentValue}`}
            onBlur={_onBlur}
            onChange={_onChange}
            onFocus={_onFocus}
          >
            {enumOptions.map((option, i) => {
              const radio = (
                <FormControlLabel
                  control={
                    <Radio color='primary' id={`${id}_${option.id}`} key={i} />
                  }
                  disabled={disabled || readonly}
                  key={i}
                  label={`${option.label}`}
                  value={`${option.id}`}
                />
              );

              return radio;
            })}
          </RadioGroup>
          <FormHelperText>{schema.description}</FormHelperText>
        </>
      ) : (
        <FormGroup row={row}>
          <Box className={classes.skeletonBox}>
            <Skeleton
              className={classes.skeletonCheckBox}
              height={20}
              variant='rect'
              width={20}
            />
            <Skeleton
              className={classes.skeletonCheckLabel}
              height={20}
              variant='rect'
              width={250}
            />
          </Box>
          <Box className={classes.skeletonBox}>
            <Skeleton
              className={classes.skeletonCheckBox}
              height={20}
              variant='rect'
              width={20}
            />
            <Skeleton
              className={classes.skeletonCheckLabel}
              height={20}
              variant='rect'
              width={250}
            />
          </Box>
          <Box className={classes.skeletonBox}>
            <Skeleton
              className={classes.skeletonCheckBox}
              height={20}
              variant='rect'
              width={20}
            />
            <Skeleton
              className={classes.skeletonCheckLabel}
              height={20}
              variant='rect'
              width={250}
            />
          </Box>
        </FormGroup>
      )}
    </>
  );
};

export default RadioField;
